import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { useBlockingInvoiceError } from 'hooks/athletePackage/useBlockingInvoiceError';
import useCancelAthletePackage from 'hooks/athletePackage/useCancelAthletePackage';
import { AthletePackageCancel } from 'types';
import notificationHandler from 'utils/notifications';

import AthletePackageCancellationErrorModal from './AthletePackageCancellationErrorModal';

interface IModalAthletePackageCancel {
  opened: boolean;
  handleClose: () => void;
  athletePackageId: string;
}

const ModalAthletePackageCancel = ({
  opened,
  athletePackageId,
  handleClose,
}: IModalAthletePackageCancel): JSX.Element => {
  const cancelAthletePackage = useCancelAthletePackage(athletePackageId);

  const { successNotification } = notificationHandler();

  const { showErrorModal, handleError, resetError } = useBlockingInvoiceError({
    nonBlockingErrorTitle: 'Package cancel failed',
  });
  const handleCancel = (): void => {
    if (athletePackageId) {
      const payload: AthletePackageCancel = {
        cancelNow: false,
        cancellationDate: null,
        athleteStatusOnCancel: null,
        cancellationReason: 'Athlete cancel',
        retainReservations: false,
      };
      cancelAthletePackage.mutate(payload, {
        onSuccess: () => {
          successNotification(
            'Package has been successfully canceled',
            `You can still use the package until it's end date`,
          );
          handleClose();
        },
        onError: error => {
          handleError(error);
        },
      });
    }
  };
  const handleErrorModalClose = (): void => {
    resetError();
    handleClose();
  };

  return (
    <>
      <Modal
        centered
        opened={opened && !showErrorModal}
        onClose={handleClose}
        title={<Title order={4}>Are you sure you want to cancel this package?</Title>}
        size='md'
      >
        <Stack>
          <Text>{`You will be able to continue using your current package until it ends.`}</Text>
          <Text>{`The auto-renew for the next cycle will be turned off.`}</Text>
          <Group position='right'>
            <Button onClick={handleClose}>Go Back</Button>
            <Button onClick={handleCancel} color='red' loading={cancelAthletePackage.isLoading}>
              Yes, Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      <AthletePackageCancellationErrorModal opened={showErrorModal} onClose={handleErrorModalClose} />
    </>
  );
};

export default ModalAthletePackageCancel;
